export type ColorId = 'default' | 'pink' | 'red' | 'orange' | 'green';

export type ColorOption = {
  id: ColorId;
  color: string;
  lightHex: string;
  darkHex: string;
};

const CURSOR_COLOR_OPTIONS: ColorOption[] = [
  { id: 'default', color: 'hglt-prim', lightHex: '#abb3fe', darkHex: '#5665f0' },
  { id: 'pink', color: 'hglt-pink-prim', lightHex: '#f093fc', darkHex: '#ab21be' },
  { id: 'red', color: 'hglt-red-prim', lightHex: '#ff7570', darkHex: '#b82929' },
  { id: 'orange', color: 'hglt-orange-prim', lightHex: '#fbb559', darkHex: '#a36319' },
  { id: 'green', color: 'hglt-green-prim', lightHex: '#5de9a5', darkHex: '#218359' }
];

const SENTENCE_COLOR_OPTIONS: ColorOption[] = [
  { id: 'default', color: 'hglt-sec', lightHex: '#e0e3ff', darkHex: '#444766' },
  { id: 'pink', color: 'hglt-pink-sec', lightHex: '#fadafe', darkHex: '#5f4064' },
  { id: 'red', color: 'hglt-red-sec', lightHex: '#ffe0dc', darkHex: '#663231' },
  { id: 'orange', color: 'hglt-orange-sec', lightHex: '#feeace', darkHex: '#4c3921' },
  { id: 'green', color: 'hglt-green-sec', lightHex: '#c7fde8', darkHex: '#254b3f' }
];

export const TRANSPARENT_COLOR = '#00000000';

export function getCursorColorOptions(isPDF: boolean = false): ColorOption[] {
  return isPDF ? CURSOR_COLOR_OPTIONS.map(option => ({ ...option, color: option.color.replace('hglt-', 'hglt-pdf-') })) : CURSOR_COLOR_OPTIONS;
}

export function getSentenceColorOptions(isPDF: boolean = false): ColorOption[] {
  return isPDF ? SENTENCE_COLOR_OPTIONS.map(option => ({ ...option, color: option.color.replace('hglt-', 'hglt-pdf-') })) : SENTENCE_COLOR_OPTIONS;
}

export function getCursorColorOption(colorId: ColorId, isPDF: boolean = false): ColorOption {
  return getCursorColorOptions(isPDF).find(option => option.id === colorId) || CURSOR_COLOR_OPTIONS[0];
}

export function getSentenceColorOption(colorId: ColorId, isPDF: boolean = false): ColorOption {
  return getSentenceColorOptions(isPDF).find(option => option.id === colorId) || SENTENCE_COLOR_OPTIONS[0];
}

export function getCursorColorBgClass(colorId: ColorId, isPDF: boolean = false): string {
  const colorOption = getCursorColorOption(colorId, isPDF);
  return `bg-${colorOption.color}`;
}

export function getSentenceColorBgClass(colorId: ColorId, isPDF: boolean = false): string {
  const colorOption = getSentenceColorOption(colorId, isPDF);
  return `bg-${colorOption.color}`;
}

export function getCursorColorVariable(colorId: ColorId, isPDF: boolean = false): string {
  const colorOption = getCursorColorOption(colorId, isPDF);
  return `var(--color-${colorOption.color})`;
}

export function getSentenceColorVariable(colorId: ColorId, isPDF: boolean = false): string {
  const colorOption = getSentenceColorOption(colorId, isPDF);
  return `var(--color-${colorOption.color})`;
}

export const getCursorColorHex = (colorId: ColorId, isDarkMode: boolean, isPDF: boolean = false): string => {
  const colorOption = getCursorColorOption(colorId, isPDF);
  return isDarkMode ? colorOption.darkHex : colorOption.lightHex;
};

export const getSentenceHighlightHex = (colorId: ColorId, isDarkMode: boolean, isPDF: boolean = false): string => {
  const colorOption = getSentenceColorOption(colorId, isPDF);
  return isDarkMode ? colorOption.darkHex : colorOption.lightHex;
};

export const getHoverBackgroundHex = (isDarkMode: boolean): string => {
  return isDarkMode ? '#2c2d2ebf' : '#e8eaf0bf';
};
